import { useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { MapPin, Clock, Truck, Upload, FileText, X } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSubmitting(true);
    try {
      await api.sendContact(formData);
      toast.success('Message sent successfully! I\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20 min-h-screen bg-void">
      {/* Header */}
      <div className="py-16 md:py-24 px-6 md:px-12 lg:px-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-2 block">
            Get in Touch
          </span>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter uppercase text-white mb-4">
            Contact Me
          </h1>
          <p className="text-neutral-400 max-w-xl">
            Have a question about a piece? Interested in a custom commission? 
            Or just want to say hello? I'd love to hear from you.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className="p-6 border border-white/10 bg-void-paper">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-cyan-400/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-mono text-sm uppercase tracking-widest text-white">
                      Location
                    </h3>
                    <p className="text-neutral-400">Shipping Worldwide</p>
                  </div>
                </div>
              </div>

              <div className="p-6 border border-white/10 bg-void-paper">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-cyan-400/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="font-mono text-sm uppercase tracking-widest text-white">
                      Response Time
                    </h3>
                    <p className="text-neutral-400">Within 24-48 hours</p>
                  </div>
                </div>
              </div>

              <div className="p-6 border border-cyan-400/30 bg-cyan-400/5">
                <h3 className="font-heading text-lg font-semibold text-white mb-2">
                  Custom Orders
                </h3>
                <p className="text-neutral-400 text-sm">
                  Looking for something unique? I accept custom commissions! 
                  Share your vision and let's create something extraordinary together.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form
                onSubmit={handleSubmit}
                className="bg-void-paper border border-white/10 p-8 md:p-12"
              >
                <h2 className="font-heading text-2xl font-bold text-white mb-8">
                  Send a Message
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      data-testid="contact-name"
                      placeholder="Your name"
                      className="w-full bg-void border border-white/10 h-14 px-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      data-testid="contact-email"
                      placeholder="your@email.com"
                      className="w-full bg-void border border-white/10 h-14 px-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    data-testid="contact-subject"
                    placeholder="What's this about?"
                    className="w-full bg-void border border-white/10 h-14 px-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors"
                  />
                </div>

                <div className="mb-8">
                  <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    data-testid="contact-message"
                    placeholder="Tell me about your inquiry..."
                    rows={6}
                    className="w-full bg-void border border-white/10 p-4 text-white placeholder:text-neutral-600 focus:border-cyan-400/50 focus:outline-none transition-colors resize-none"
                  />
                </div>

                {/* Upload Documents Section */}
                <div className="mb-8">
                  <label className="block text-sm font-mono uppercase tracking-widest text-neutral-400 mb-2">
                    Upload Documents
                  </label>
                  <p className="text-neutral-500 text-xs mb-4">Attach any relevant files (PDF, images)</p>
                  
                  <div className="space-y-3">
                    {uploadedDocs.map((doc, index) => (
                      <div key={index} className="flex items-center gap-3 bg-void border border-white/10 p-3">
                        <FileText className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                        <span className="text-white text-sm truncate flex-1">{doc.name}</span>
                        <button
                          type="button"
                          onClick={() => setUploadedDocs(prev => prev.filter((_, i) => i !== index))}
                          className="p-1 text-red-400 hover:text-red-300 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    
                    {uploadedDocs.length < 3 && (
                      <label className="flex items-center justify-center gap-3 bg-void border border-dashed border-white/20 p-4 cursor-pointer hover:border-cyan-400/50 transition-colors">
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            setUploading(true);
                            try {
                              const result = await api.uploadImage(file);
                              const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
                              setUploadedDocs(prev => [...prev, { name: file.name, url: fullUrl }]);
                            } catch (error) {
                              toast.error(`Failed to upload ${file.name}`);
                            }
                            setUploading(false);
                            e.target.value = '';
                          }}
                        />
                        {uploading ? (
                          <div className="animate-spin w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full" />
                        ) : (
                          <>
                            <Upload className="w-5 h-5 text-neutral-500" />
                            <span className="text-neutral-500 text-sm">Upload Documents</span>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting}
                  className="w-full md:w-auto bg-cyan-400 text-white px-12 py-4 font-mono uppercase tracking-widest text-sm hover:bg-cyan-400-hover transition-all duration-300 hover:shadow-[0_0_20px_rgba(217,70,239,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Shipping Policy Section */}
      <div className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-void-paper border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
              <Truck className="w-6 h-6 text-cyan-400" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white">
              Shipping Policy
            </h2>
          </div>
          
          <div className="space-y-6 text-neutral-300 leading-relaxed">
            <p>
              At Only Skullz LLC, we ship fast and ship right. Orders are processed within 1–3 business days and sent using trusted carriers through Shippo to lock in the best rates and reliable delivery.
            </p>
            <p>
              Shipping costs are shown at checkout—no hidden fees, no games. Every order comes with real-time tracking, so you'll know exactly where your gear is and when it's landing.
            </p>
            <p>
              Make sure your shipping info is correct. Once an order ships, we can't reroute it or replace it due to address errors. If a package is delayed or marked delivered but missing, hit us up and we'll help you deal with the carrier.
            </p>
            <p className="text-cyan-400 font-medium">
              No fluff. No excuses. Just delivery done right.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
